import type { ApiResult } from '../types';
import type { AttorneyGeneralRequestViewData } from './attorneyGeneralCaseRequests';
import type { CivilAdviseRequestViewData } from './civilAdviseCaseRequests';
import type { CivilAppellateRequestViewData } from './civilAppellateCaseRequests';
import type { CivilLitigationRequestViewData } from './civilLitigationCaseRequests';
import type { CriminalRequestViewData } from './criminalCaseRequests';
import type { CriminalAppellateRequestViewData } from './criminalAppellateCaseRequests';
import type { ExtraditionMlaRequestViewData } from './extraditionMlaCaseRequests';
import type { NominationRequestViewData } from './nominationCaseRequests';
import type { PublicPetitionRequestViewData } from './publicPetitionCaseRequests';
import type { RightToInformationRequestViewData } from './rightToInformationCaseRequests';
import * as attorneyGeneralService from './attorneyGeneralCaseRequests';
import * as civilAdviseService from './civilAdviseCaseRequests';
import * as civilAppellateService from './civilAppellateCaseRequests';
import * as civilLitigationService from './civilLitigationCaseRequests';
import * as criminalAppellateService from './criminalAppellateCaseRequests';
import * as criminalService from './criminalCaseRequests';
import * as extraditionMlaService from './extraditionMlaCaseRequests';
import * as nominationService from './nominationCaseRequests';
import * as publicPetitionService from './publicPetitionCaseRequests';
import * as rightToInformationService from './rightToInformationCaseRequests';
import type { CaseRequestViewOptions } from './caseRequestViewOptions';
import {
  getCaseRequestHeader,
  headerCategory,
  headerReferenceNo,
  headerSubCategory,
  headerTaskStatus,
} from './caseRequestDetails';
import type { CaseRequestHeaderResponse } from '../types/caseRequestDetails';
import type { WizardFlowKind } from '../utils/wizardCategoryRoute';
import { wizardFlowForCategoryName } from '../utils/wizardCategoryRoute';

export type CaseRequestViewPayload =
  | { flow: 'criminal'; data: CriminalRequestViewData }
  | { flow: 'criminal-appellate'; data: CriminalAppellateRequestViewData }
  | { flow: 'civil-advise'; data: CivilAdviseRequestViewData }
  | { flow: 'civil-litigation'; data: CivilLitigationRequestViewData }
  | { flow: 'civil-appellate'; data: CivilAppellateRequestViewData }
  | { flow: 'extradition-mla'; data: ExtraditionMlaRequestViewData }
  | { flow: 'nomination-requests'; data: NominationRequestViewData }
  | { flow: 'public-petition'; data: PublicPetitionRequestViewData }
  | { flow: 'right-to-information'; data: RightToInformationRequestViewData }
  | { flow: 'attorney-general'; data: AttorneyGeneralRequestViewData };

type DraftWithHeaderFields = {
  referenceNo?: string;
  fileCategory?: string;
  subFileCategoryName?: string;
};

function mergeHeaderIntoViewData<T extends { statusName: string; draft: DraftWithHeaderFields }>(
  data: T,
  header: CaseRequestHeaderResponse | undefined,
): T {
  if (!header) return data;
  return {
    ...data,
    statusName: headerTaskStatus(header) || data.statusName,
    draft: {
      ...data.draft,
      referenceNo: headerReferenceNo(header) || data.draft.referenceNo,
      fileCategory: headerCategory(header) || data.draft.fileCategory,
      subFileCategoryName: headerSubCategory(header) || data.draft.subFileCategoryName,
    },
  };
}

async function loadViewDataForFlow(
  flow: WizardFlowKind,
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CaseRequestViewPayload | null>> {
  switch (flow) {
    case 'civil-advise': {
      const res = await civilAdviseService.getCivilAdviseRequestViewData(caseRequestId, options);
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'civil-advise', data: res.data } };
    }
    case 'civil-litigation': {
      const res = await civilLitigationService.getCivilLitigationRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'civil-litigation', data: res.data } };
    }
    case 'civil-appellate': {
      const res = await civilAppellateService.getCivilAppellateRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'civil-appellate', data: res.data } };
    }
    case 'extradition-mla': {
      const res = await extraditionMlaService.getExtraditionMlaRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'extradition-mla', data: res.data } };
    }
    case 'nomination-requests': {
      const res = await nominationService.getNominationRequestViewData(caseRequestId, options);
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'nomination-requests', data: res.data } };
    }
    case 'criminal-appellate': {
      const res = await criminalAppellateService.getCriminalAppellateRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'criminal-appellate', data: res.data } };
    }
    case 'public-petition': {
      const res = await publicPetitionService.getPublicPetitionRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'public-petition', data: res.data } };
    }
    case 'right-to-information': {
      const res = await rightToInformationService.getRightToInformationRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'right-to-information', data: res.data } };
    }
    case 'attorney-general': {
      const res = await attorneyGeneralService.getAttorneyGeneralRequestViewData(
        caseRequestId,
        options,
      );
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'attorney-general', data: res.data } };
    }
    case 'criminal': {
      const res = await criminalService.getCriminalRequestViewData(caseRequestId, options);
      if (!res.success) return res;
      if (!res.data) return { success: true, data: null };
      return { success: true, data: { flow: 'criminal', data: res.data } };
    }
    case 'unknown':
    default:
      return { success: true, data: null };
  }
}

/** Resolve case type from header and load the matching steps GET payload. */
export async function loadCaseRequestViewDataFromHeader(
  caseRequestId: string,
  header: CaseRequestHeaderResponse,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<{ flow: WizardFlowKind; payload: CaseRequestViewPayload | null }>> {
  const id = caseRequestId.trim();
  if (!id) return { success: true, data: { flow: 'unknown', payload: null } };

  const flow = wizardFlowForCategoryName(headerCategory(header));
  const viewRes = await loadViewDataForFlow(flow, id, options);
  if (!viewRes.success) return viewRes;

  if (!viewRes.data) {
    return { success: true, data: { flow, payload: null } };
  }

  const merged = mergeHeaderIntoViewData(viewRes.data.data, header);
  return {
    success: true,
    data: {
      flow,
      payload: { flow: viewRes.data.flow, data: merged } as CaseRequestViewPayload,
    },
  };
}

/** Resolve case type from header and load the matching steps GET payload for View Case Request. */
export async function loadCaseRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<{ flow: WizardFlowKind; payload: CaseRequestViewPayload | null }>> {
  const id = caseRequestId.trim();
  if (!id) return { success: true, data: { flow: 'unknown', payload: null } };

  const headerRes = await getCaseRequestHeader(id);
  if (!headerRes.success) {
    return { success: false, message: headerRes.message ?? 'Failed to load case request header.' };
  }

  return loadCaseRequestViewDataFromHeader(id, headerRes.data, options);
}
